import { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from 'react';
import ChatOutlineIcon from 'mdi-react/ChatOutlineIcon';
import { AlcaDeCima, AlcaDoCanto, AlcaEsquerda, Botao, Carregando, Painel } from './ChatIaFlutuante.styles';
import { LimiteDeErroDaJanela } from './LimiteDeErroDaJanela';
import { useTamanhoDaJanela } from './useTamanhoDaJanela';

// A janela (assistant-ui, AG-UI, markdown) só é baixada no primeiro clique.
// O `lazy` guarda a rejeição em cache; por isso "Tentar de novo" recria o componente.
const criarJanelaSobDemanda = () =>
    lazy(() => import('./ChatIaJanela').then((modulo) => ({ default: modulo.ChatIaJanela })));

const SELETOR_DO_CAMPO = 'textarea';

/**
 * Botão flutuante do assistente. O primeiro clique carrega a `ChatIaJanela` (`React.lazy`) e ela fica montada
 * até a página recarregar: fechar só esconde o painel, então uma resposta em andamento continua chegando.
 * Esc (com o foco na janela) e o "Fechar" da janela fecham e devolvem o foco ao botão.
 * O tamanho muda arrastando o canto/bordas de cima e da esquerda (ou pelas setas, com o foco no canto) e pelo
 * botão expandir do cabeçalho; duplo clique no canto volta ao padrão. O navegador lembra o tamanho.
 */
export const ChatIaFlutuante = ({ urlApi, obterToken, contexto, margemInferior = 16, aoNaoAutorizado }) => {
    const idDoPainel = useId();
    const [aberto, setAberto] = useState(false);
    const [jaAbriu, setJaAbriu] = useState(false);
    const [ChatIaJanelaSobDemanda, setJanelaSobDemanda] = useState(criarJanelaSobDemanda);
    const botaoRef = useRef(null);
    const painelRef = useRef(null);
    const tamanho = useTamanhoDaJanela({ margemInferior, painelRef });

    const abrir = useCallback(() => {
        setJaAbriu(true);
        setAberto(true);
    }, []);

    const fechar = useCallback(() => {
        setAberto(false);
        botaoRef.current?.focus();
    }, []);

    // Ao abrir, o foco vai para o campo de mensagem; enquanto a janela carrega (lazy, agentes), fica no painel
    // e passa ao campo assim que ele aparece, desde que o usuário não tenha movido o foco para outro lugar.
    useEffect(() => {
        const painel = painelRef.current;
        if (!aberto || !painel) return undefined;

        if (!painel.contains(document.activeElement)) {
            (painel.querySelector(SELETOR_DO_CAMPO) ?? painel).focus();
        }
        if (painel.querySelector(SELETOR_DO_CAMPO)) return undefined;

        const observador = new MutationObserver(() => {
            const campo = painel.querySelector(SELETOR_DO_CAMPO);
            if (!campo) return;
            if (document.activeElement === painel || document.activeElement === document.body) campo.focus();
            observador.disconnect();
        });
        observador.observe(painel, { childList: true, subtree: true });
        return () => observador.disconnect();
    }, [aberto, jaAbriu]);

    const aoTeclar = (evento) => {
        if (evento.key === 'Escape' && aberto && !evento.defaultPrevented) {
            evento.stopPropagation();
            fechar();
        }
    };

    return (
        <>
            <Botao
                ref={botaoRef}
                type="button"
                aria-label="Abrir assistente Transben"
                aria-expanded={aberto}
                aria-controls={jaAbriu ? idDoPainel : undefined}
                title="Assistente Transben"
                $margemInferior={margemInferior}
                onClick={aberto ? fechar : abrir}
            >
                <ChatOutlineIcon size={28} aria-hidden="true" />
            </Botao>
            {jaAbriu && (
                <Painel
                    ref={painelRef}
                    id={idDoPainel}
                    role="dialog"
                    aria-label="Assistente Transben"
                    tabIndex={-1}
                    hidden={!aberto}
                    $margemInferior={margemInferior}
                    data-expandida={tamanho.expandida}
                    style={{
                        '--chat-ia-largura': `${tamanho.largura}px`,
                        '--chat-ia-altura': `${tamanho.altura}px`,
                    }}
                    onKeyDown={aoTeclar}
                >
                    <AlcaEsquerda aria-hidden="true" onMouseDown={(evento) => tamanho.iniciarArraste(evento, 'largura')} />
                    <AlcaDeCima aria-hidden="true" onMouseDown={(evento) => tamanho.iniciarArraste(evento, 'altura')} />
                    <AlcaDoCanto
                        aria-label="Redimensionar janela"
                        title="Arraste para redimensionar (ou use as setas). Duplo clique volta ao tamanho padrão."
                        onMouseDown={(evento) => tamanho.iniciarArraste(evento)}
                        onKeyDown={tamanho.redimensionarPeloTeclado}
                        onDoubleClick={tamanho.restaurarPadrao}
                    />
                    <LimiteDeErroDaJanela aoTentarDeNovo={() => setJanelaSobDemanda(criarJanelaSobDemanda)}>
                        <Suspense fallback={<Carregando>Carregando assistente…</Carregando>}>
                            <ChatIaJanelaSobDemanda
                                urlApi={urlApi}
                                obterToken={obterToken}
                                contexto={contexto}
                                aoFechar={fechar}
                                aoNaoAutorizado={aoNaoAutorizado}
                                expandida={tamanho.expandida}
                                aoAlternarExpansao={tamanho.alternarExpansao}
                            />
                        </Suspense>
                    </LimiteDeErroDaJanela>
                </Painel>
            )}
        </>
    );
};
